import { useState } from 'react';
import { Table, Card } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import styles from '../styles/Activity.module.scss';
import { TableState } from '@components/Activity/types/TableState';
import TableRowWithRadioButton from './TableRowWithRadioButton';
import GridColumnInputWrapper from '@components/common/GridColumnInputWrapper';
import { InputWrapper } from 'react-lib/dist/components-es';
import { EnumLikelihood } from './EnumLikelihood';
import { CLONE_MODE, CREATE_MODE, EDIT_MODE, VIEW_MODE } from '@src/constants';
import React from 'react';

type Props = Partial<TableState> & {
  riskAssessment?: any;
  disabledComponent?: boolean;
  mode?: string;
  [key: string]: any;
};

const RiskTableContainer = ({ riskAssessment, disabledComponent, mode }: Props) => {
  const { t } = useTranslation();
  const stringToEnumLikelihood = (likelihood?: string): string | undefined => {
    switch (likelihood) {
      case 'LOW':
        return `${EnumLikelihood.LOW}`;
      case 'MEDIUM':
        return `${EnumLikelihood.MEDIUM}`;
      case 'HIGH':
        return `${EnumLikelihood.HIGH}`;
      default:
        return undefined;
    }
  };

  const renderTableRow = (label: string, name: string, sourceKey: string) => {
    const presentKey = `set${name}Present`;
    const likelihoodSetter = (val: string) => {
      if (riskAssessment && riskAssessment[presentKey]) {
        const setter = riskAssessment[presentKey];
        setter(val);
      }
    };
    const ignitionKey = `set${name}Ignition`;
    const ignitionSetter = (val: string) => {
      if (riskAssessment && riskAssessment[ignitionKey]) {
        const setter = riskAssessment[ignitionKey];
        setter(val);
      }
    };
    let ignitionValue;
    let likelihoodValue;

    //if risk assessment is taken from DB key has value: ["HIGH", "MEDIUM", "LOW"], during editing data and creating form it has value: ["2", "1", "0"]- it's because of enum
    switch (mode) {
      case CREATE_MODE:
        ignitionValue = riskAssessment?.riskAssessmentContainer?.ignitionLikelihood?.[sourceKey];
        likelihoodValue = riskAssessment?.riskAssessmentContainer?.sourcePresent?.[sourceKey];
        break;
      case VIEW_MODE:
        ignitionValue = stringToEnumLikelihood(riskAssessment?.ignitionLikelihood?.[sourceKey]);
        likelihoodValue = stringToEnumLikelihood(riskAssessment?.sourcePresent?.[sourceKey]);
        break;
      case CLONE_MODE:
      case EDIT_MODE:
        const ignitionKeyLength = riskAssessment?.ignitionLikelihood?.[sourceKey]?.length;
        const isIgnitionString = ignitionKeyLength > 1;

        ignitionValue = isIgnitionString
          ? stringToEnumLikelihood(riskAssessment?.ignitionLikelihood?.[sourceKey])
          : riskAssessment?.ignitionLikelihood?.[sourceKey];

        const likelihoodKeyLength = riskAssessment?.sourcePresent?.[sourceKey]?.length;
        const isLikelihoodString = likelihoodKeyLength > 1;

        likelihoodValue = isLikelihoodString
          ? stringToEnumLikelihood(riskAssessment?.sourcePresent?.[sourceKey])
          : riskAssessment?.sourcePresent?.[sourceKey];

        break;
      default:
        break;
    }

    return (
      <TableRowWithRadioButton
        label={t(label)}
        likelihoodValue={likelihoodValue}
        likelihoodSetter={likelihoodSetter}
        ignitionValue={ignitionValue}
        ignitionSetter={ignitionSetter}
        name={sourceKey}
        disabledComponent={disabledComponent}
      />
    );
  };

  return (
    <div className={styles.activityTextContainer}>
      <Card className={styles.cardText}>
        <div className={`${styles.basicDiv} ${styles.tableContainer}`}>
          <div className={styles.riskTextHeader}>
            {t('risk.assessment.details.page.risk.details.container.table.title')}
          </div>
          <Table definition>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className={styles.hiddenCel} />
                <Table.HeaderCell>
                  {t('risk.assessment.details.page.risk.details.container.table.header.column1')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('risk.assessment.details.page.risk.details.container.table.header.column2')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.flames',
                'Flames',
                'flames'
              )}
              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.hotSurface',
                'HotSurface',
                'hotSurface'
              )}
              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.mechanical',
                'Sparks',
                'sparks'
              )}
              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.chemical',
                'ChemReaction',
                'chemReaction'
              )}
              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.electrical',
                'ElectricalApparatus',
                'electricalApparatus'
              )}

              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.staticElectricity',
                'Electricity',
                'electricity'
              )}
              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.electromagneticRadiation',
                'ElectromagneticRadiation',
                'electromagneticRadiation'
              )}
              {renderTableRow(
                'risk.assessment.details.page.risk.details.container.table.row.ionisingRadiation',
                'IonisingRadiation',
                'ionisingRadiation'
              )}

              {(riskAssessment.setOtherSource || riskAssessment?.ignitionLikelihood?.otherTxt) && (
                <TableRowWithRadioButton
                  label={t('risk.assessment.details.page.risk.details.container.table.row.other')}
                  likelihoodValue={
                    mode == CREATE_MODE
                      ? riskAssessment.otherPresent
                      : riskAssessment?.sourcePresent?.otherPresent.length > 1
                      ? stringToEnumLikelihood(riskAssessment?.sourcePresent?.otherPresent)
                      : riskAssessment?.sourcePresent?.otherPresent
                  }
                  likelihoodSetter={riskAssessment.setOtherPresent}
                  ignitionValue={
                    mode == CREATE_MODE
                      ? riskAssessment.otherIgnition
                      : riskAssessment?.ignitionLikelihood?.otherIgnition.length > 1
                      ? stringToEnumLikelihood(riskAssessment?.ignitionLikelihood?.otherIgnition)
                      : riskAssessment?.ignitionLikelihood?.otherIgnition
                  }
                  ignitionSetter={riskAssessment.setOtherIgnition}
                  name="other"
                  otherTable={riskAssessment.otherTable}
                  setOtherTable={riskAssessment.setOtherTable}
                  disabled={
                    mode == CREATE_MODE || mode == EDIT_MODE
                      ? riskAssessment.disabled
                      : disabledComponent
                  }
                  setDisabled={riskAssessment.setDisabled}
                  disabledComponent={disabledComponent}
                />
              )}
            </Table.Body>
          </Table>
          {((riskAssessment.otherTable && riskAssessment.setOtherSource) ||
            (mode == VIEW_MODE && riskAssessment?.ignitionLikelihood?.other)) && (
            <div className={styles.checkboxContainerText}>
              <GridColumnInputWrapper
                value={
                  mode == CREATE_MODE
                    ? riskAssessment.otherSource
                    : riskAssessment?.ignitionLikelihood?.otherTxt
                }
                rows={1}
                disabled={disabledComponent}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (riskAssessment.setOtherSource) {
                    riskAssessment.setOtherSource(e.target.value);
                  }
                }}
                render={(props: any) => (
                  <InputWrapper
                    {...props}
                    placeholder={t(
                      'risk.assessment.details.page.risk.details.container.table.other.text'
                    )}
                  />
                )}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default RiskTableContainer;
